import React from "react";
import { NavLink as MantineNavLink, Tooltip, Menu } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { withRouter } from "../utilities/withRouter";
import { connect } from "react-redux";
import "./LeftSidebar.css";

import { toggleSidenav } from "../actions/browserLocalstorageActions";
import {
	IconAsterisk,
	IconBriefcase,
	IconBuilding,
	IconChevronLeft,
	IconChevronRight,
	IconCrown,
	IconFlask,
	IconFolderSymlink,
	IconHelpCircle,
	IconHome,
	IconKey,
	IconListSearch,
	IconSettings,
	IconShare2,
	IconSparkles,
	IconTrash,
	IconUsersGroup,
	IconMap,
	IconDeviceImacCode,
	IconFolders,
} from "@tabler/icons-react";

const sidebarIconProps = { size: 20, style: { verticalAlign: "middle" } };

const sidenavOptions = [
	{
		eventKey: "/",
		pathExact: "/",
	},
	{ eventKey: "/clients" },
	{ eventKey: "/matters" },
	{
		eventKey: "/firmDocs/public/docs/",
		pathPrefix: "/firmDocs",
	},
	{ eventKey: "/search" },
	{
		eventKey: "/sharing/outgoing",
		pathPrefix: "/sharing",
	},
	{ eventKey: "/ai" },
	{ eventKey: "/myFirm" },
	{ eventKey: "/settings" },
	{ eventKey: "/admin" },
	{ eventKey: "/superAdmin" },
	{
		eventKey: "/trash/clients",
		pathPrefix: "/trash",
	},
];

function checkSelectedItem(pathname) {
	const selectedItem = sidenavOptions.find((item) => {
		if (item.pathExact) {
			return pathname === item.pathExact;
		} else if (item.pathPrefix) {
			return pathname.indexOf(item.pathPrefix) === 0;
		} else {
			return pathname.indexOf(item.eventKey) === 0;
		}
	});

	return selectedItem ? selectedItem.eventKey : undefined;
}

const developmentSubItems = [
	{
		label: "Product Roadmap",
		href: "https://lexworkplace.canny.io",
		icon: <IconMap {...sidebarIconProps} size={16} />,
	},
	{
		label: "What's New",
		href: "https://lexworkplace.com/whats-new",
		icon: <IconAsterisk {...sidebarIconProps} size={16} />,
	},
	{
		label: "Request a Feature",
		href: "https://lexworkplace.canny.io/feature-requests",
		icon: <IconFlask {...sidebarIconProps} size={16} />,
	},
];

const firmDocsSubItems = [
	{
		label: "Public Firm Docs",
		href: "/firmDocs/public/docs/",
		icon: <IconFolderSymlink {...sidebarIconProps} size={16} />,
	},
	{
		label: "Private Firm Docs",
		href: "/firmDocs/private/docs/",
		icon: <IconFolderSymlink {...sidebarIconProps} size={16} />,
	},
];

const getFirmDocsNavItems = (canAccessPrivateFirmDocs) => {
	if (canAccessPrivateFirmDocs) {
		return {
			key: "/firmDocs/public/docs/",
			label: "Firm Docs",
			icon: <IconFolders {...sidebarIconProps} />,
			submenu: true,
			submenuItems: firmDocsSubItems,
		};
	} else {
		return {
			key: "/firmDocs/public/docs/",
			label: "Public Firm Docs",
			icon: <IconFolderSymlink {...sidebarIconProps} />,
		};
	}
};

const LeftSidebar = (props) => {
	const isMobile = useMediaQuery("(max-width: 767px)");
	const { history } = props;
	const sidenavExpanded = isMobile || props.sidenavExpanded;
	const selectedKey = checkSelectedItem(props.browserLocationPathname);

	const navigate = (eventKey) => {
		if (eventKey.match(/^http/)) {
			window.open(eventKey, "_blank");
		} else {
			history.push(eventKey);
			if (props.closeMobileSidebar) {
				props.closeMobileSidebar();
			}
		}
	};

	const firmDocsNavItem = getFirmDocsNavItems(props.canAccessPrivateFirmDocs);

	const navItems = [
		{
			key: "/",
			label: "Home",
			icon: <IconHome {...sidebarIconProps} />,
		},
		{
			key: "/clients",
			label: "Clients",
			icon: <IconUsersGroup {...sidebarIconProps} />,
		},
		{
			key: "/matters",
			label: "Matters",
			icon: <IconBriefcase {...sidebarIconProps} />,
		},
		firmDocsNavItem,
		{
			key: "/search",
			label: "Search",
			icon: <IconListSearch {...sidebarIconProps} />,
		},
		{
			key: "/sharing/outgoing",
			label: "Sharing",
			icon: <IconShare2 {...sidebarIconProps} />,
		},
		{
			key: "/ai",
			label: "LexWorkplace AI",
			icon: <IconSparkles {...sidebarIconProps} fill={"currentColor"} />,
		},
		{
			key: "/myFirm",
			label: "My Firm",
			icon: <IconBuilding {...sidebarIconProps} />,
		},
		{
			key: "https://help.lexworkplace.com/",
			label: "Help Center",
			icon: <IconHelpCircle {...sidebarIconProps} />,
			external: true,
		},
		{
			key: "development",
			label: "Development",
			icon: <IconDeviceImacCode {...sidebarIconProps} />,
			submenu: true,
			submenuItems: developmentSubItems,
		},
		{
			key: "/settings",
			label: "Settings",
			icon: <IconSettings {...sidebarIconProps} />,
		},
	];

	const conditionalItems = [];
	if (props.isLexworkplaceAdmin) {
		conditionalItems.push({
			key: "/admin",
			label: "Admin",
			icon: <IconKey {...sidebarIconProps} />,
		});
	}
	if (props.isSuperAdmin) {
		conditionalItems.push({
			key: "/superAdmin",
			label: "Super Admin",
			icon: <IconCrown {...sidebarIconProps} />,
		});
	}
	if (props.userRights.CAN_PERMANENT_DELETE) {
		conditionalItems.push({
			key: "/trash/clients",
			label: "Trash Bin",
			icon: <IconTrash {...sidebarIconProps} />,
		});
	}

	const allItems = [...navItems, ...conditionalItems];

	const renderNavItem = (item) => {
		if (item.submenu) {
			return renderSubmenuItems(item);
		}

		const isActive = selectedKey === item.key;

		if (!sidenavExpanded) {
			return (
				<Tooltip
					label={item.label}
					position="right"
					withArrow
					key={item.key}
				>
					<MantineNavLink
						className={
							"LeftSidebarNavLink" + (isActive ? " active" : "")
						}
						label={item.icon}
						onClick={() => navigate(item.key)}
						active={isActive}
						variant="filled"
					/>
				</Tooltip>
			);
		}

		return (
			<MantineNavLink
				key={item.key}
				className={"LeftSidebarNavLink" + (isActive ? " active" : "")}
				label={item.label}
				leftSection={item.icon}
				onClick={() => navigate(item.key)}
				active={isActive}
				variant="filled"
			/>
		);
	};

	const renderSubmenuItems = ({ label, submenuItems, icon }) => {
		if (!sidenavExpanded) {
			return (
				<Menu key={label} position="right-start" withArrow shadow="md">
					<Menu.Target>
						<Tooltip label={label} position="right" withArrow>
							<MantineNavLink
								className="LeftSidebarNavLink"
								label={icon}
							/>
						</Tooltip>
					</Menu.Target>
					<Menu.Dropdown>
						{submenuItems.map((sub) => (
							<Menu.Item
								key={sub.href}
								leftSection={sub.icon}
								onClick={() => navigate(sub.href)}
							>
								{sub.label}
							</Menu.Item>
						))}
					</Menu.Dropdown>
				</Menu>
			);
		}

		return (
			<MantineNavLink
				key={label}
				className="LeftSidebarNavLink"
				label={label}
				leftSection={icon}
				childrenOffset={0}
			>
				{submenuItems.map((sub) => (
					<MantineNavLink
						key={sub.href}
						className="LeftSidebarNavLink LeftSidebarSubNavLink"
						label={sub.label}
						leftSection={sub.icon}
						onClick={() => navigate(sub.href)}
					/>
				))}
			</MantineNavLink>
		);
	};

	return (
		<div className={"LeftSidebar" + (sidenavExpanded ? " expanded" : "")}>
			{!isMobile ? (
				<div
					className="LeftSidebarToggle"
					onClick={props.toggleSidenav}
				>
					{sidenavExpanded ? (
						<IconChevronLeft size={18} />
					) : (
						<IconChevronRight size={18} />
					)}
				</div>
			) : null}
			<div className="LeftSidebarNav">{allItems.map(renderNavItem)}</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		browserLocationPathname: state.globalSearchbar.location,
		sidenavExpanded: state.browserLocalstorage.sidenavExpanded,
		userRights: state.user.userRights,
		canAccessPrivateFirmDocs: state.user.canAccessPrivateFirmDocs,
	};
};
const LeftSidebarWithRouter = withRouter(
	connect(mapStateToProps, { toggleSidenav })(LeftSidebar)
);
export { LeftSidebarWithRouter };
